import React from "react";
import TextInput from "../common/TextInput";
import { BeatLoader } from "react-spinners";
import { useTranslation } from "react-i18next";
import tailwindClassnames from "../common/TailwindClassnames";
import { LocationCreateUpdateDto } from "../common/ObjectTypes";
import LatLongAltCoordinateInput from "../common/LatLonAltCoordinateInput";
import SelectInput from "../common/SelectInput";
import { getCountryCodeFromCoords } from "../../api/locationsApi";
import Map from "../common/Map";

interface AddLocationFormProps {
  location: LocationCreateUpdateDto;
  locationLoading: boolean;
  countries: any;
  onClose: () => void;
  onSave: Function;
  errors: any;
  saveError?: string;
  onChange: (e: any) => void;
  onSelectCountry: (e: any) => void;
  saving: boolean;
  savingAndAddAnother: boolean;
  existingLocation: boolean;
  onKeyPress?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  getLocationErrors: (location: LocationCreateUpdateDto) => Object;
  viewOnly: boolean;
  saveDisabled: boolean;
  /**
   * Opens the CSV import (AKL-814). Supplied by the parent only when the user may CREATE
   * locations; the button is gated on this prop, not on `viewOnly`, because editing
   * (EDIT_DEPLOYMENTS_LOCATIONS) is a different permission. Only offered on the Add form —
   * an existing location has nothing to import.
   */
  onImportFromCsv?: () => void;
}

const LocationForm: React.FC<AddLocationFormProps> = ({
  location,
  locationLoading,
  countries,
  onClose,
  onSave,
  onChange,
  onSelectCountry,
  errors,
  saveError,
  saving,
  savingAndAddAnother,
  existingLocation,
  onKeyPress,
  getLocationErrors,
  viewOnly,
  saveDisabled,
  onImportFromCsv,
}) => {
  const { t } = useTranslation();
  const maNameLength = 255;
  const defaultCountry = countries.find(
    (country: any) => country.code === location.country_id,
  );

  /*
   * Executes the onBlur event for the latitude and longitude fields.
   */
  const onBlur = () => {
    if (location.latitude && location.longitude) {
      const location_errors = getLocationErrors(location);
      if (Object.keys(location_errors).length === 0) {
        autoPopulateLocationNameFromCoordinates();
        autoPopulateCountryNameFromCoordinates();
      }
    }
  };

  /*
   * Automatically populates the location name field with the latitude and longitude values
   * if the location name field is empty.
   */
  const autoPopulateLocationNameFromCoordinates = () => {
    if (!location.location_name) {
      onChange({
        target: {
          name: "location_name",
          value: `${location.latitude}, ${location.longitude}`,
        },
      });
    }
  };

  /*
   * Automatically populates the country field with the country name from the latitude and longitude values
   * if the country field is empty.
   */
  const autoPopulateCountryNameFromCoordinates = async () => {
    if (!location.country_id && location.latitude && location.longitude) {
      const countryCode = await getCountryCodeFromCoords(
        location.latitude,
        location.longitude,
      );
      if (countryCode) {
        const country = countries.find(
          (country: any) =>
            country.code.toUpperCase() === countryCode.toUpperCase(),
        );
        if (country) {
          onSelectCountry({
            value: country.code,
            label: country.name,
          });
        }
      }
    }
  };

  return (
    <div className={`${tailwindClassnames.modalOverlay} z-50`}>
      <div className={`${tailwindClassnames.modalPanel} max-w-3xl`}>
        <div className={tailwindClassnames.modalHeaderRow}>
          <div className="flex items-center gap-3">
            <h2 className={tailwindClassnames.modalTitle}>
              {existingLocation ? t("Update Location") : t("Add Location")}
            </h2>
          </div>
          <button
            type="button"
            className={tailwindClassnames.modalCloseButton}
            onClick={onClose}
            aria-label={t("Close")}
          >
            &times;
          </button>
        </div>

        {locationLoading ? (
          <div className={tailwindClassnames.modalBody}>
            <BeatLoader
              color={tailwindClassnames.skyBlue}
              loading={true}
              size={10}
              margin={5}
            />
          </div>
        ) : (
          <>
            <div className={tailwindClassnames.modalBody}>
              {saveError && (
                <div className="bg-primary-orange-light rounded text-primary-orange-dark text-sm px-3 py-2 my-1">
                  {saveError}
                </div>
              )}
              <div className="mb-3">
                <TextInput
                  name="location_name"
                  label={`${t("Name")}*`}
                  placeholder={t("Enter Location name")}
                  value={location.location_name || ""}
                  disabled={viewOnly}
                  className={
                    viewOnly
                      ? tailwindClassnames.detailsPageTextInputDisabled
                      : tailwindClassnames.detailsPageTextInput
                  }
                  limit={maNameLength}
                  type="text"
                  onChange={onChange}
                  error={errors.location_name}
                  onKeyPress={onKeyPress}
                />
              </div>
              <LatLongAltCoordinateInput
                location={location}
                errors={errors}
                className={
                  viewOnly
                    ? tailwindClassnames.detailsPageTextInputDisabled
                    : tailwindClassnames.detailsPageTextInput
                }
                onChange={onChange}
                onKeyPress={onKeyPress}
                onBlur={onBlur}
                disabled={viewOnly}
              />
              <div className="mb-3 mt-3">
                <SelectInput
                  options={countries.map((country: any) => ({
                    value: country.code,
                    label: country.name,
                  }))}
                  defaultValue={
                    defaultCountry
                      ? {
                          value: defaultCountry.code,
                          label: defaultCountry.name,
                        }
                      : undefined
                  }
                  value={
                    defaultCountry
                      ? {
                          value: defaultCountry.code,
                          label: defaultCountry.name,
                        }
                      : undefined
                  }
                  className={" mr-1 w-full"}
                  isDisabled={viewOnly}
                  errors={errors.country_id}
                  isLoading={false}
                  isClearable={true}
                  isSearchable={true}
                  isMulti={false}
                  onChange={onSelectCountry}
                  name={t("Country")}
                  searchBar={false}
                />
              </div>
              <div>
                <Map
                  latitude={location.latitude}
                  longitude={location.longitude}
                />
              </div>
            </div>

            <div className={tailwindClassnames.modalFooter}>
              <button
                type="button"
                onClick={onClose}
                className={tailwindClassnames.btnSecondary}
              >
                {viewOnly ? t("Close") : t("Cancel")}
              </button>
              {!existingLocation && onImportFromCsv && (
                <button
                  type="button"
                  onClick={onImportFromCsv}
                  disabled={saving || savingAndAddAnother}
                  className={tailwindClassnames.btnSecondary}
                  data-testid="import-locations-from-csv"
                >
                  {t("Import from CSV")}
                </button>
              )}
              {!existingLocation && (
                <button
                  type="button"
                  onClick={() => onSave(true)}
                  disabled={saveDisabled || saving || savingAndAddAnother}
                  className={`relative ${tailwindClassnames.btnSecondary}`}
                >
                  {/* Label stays in layout while saving so the button keeps
                      its width; currentColor spinner reads on the primary
                      fill in both themes. */}
                  <span className={savingAndAddAnother ? "invisible" : ""}>
                    {t("Save and Add Another")}
                  </span>
                  {savingAndAddAnother && (
                    <span className="absolute inset-0 flex items-center justify-center">
                      <BeatLoader
                        color="currentColor"
                        loading={true}
                        size={10}
                        margin={5}
                      />
                    </span>
                  )}
                </button>
              )}
              {!viewOnly && (
                <button
                  type="button"
                  onClick={() => onSave(false)}
                  disabled={saveDisabled || saving || savingAndAddAnother}
                  className={`relative ${tailwindClassnames.btnPrimary}`}
                >
                  {/* Label stays in layout while saving so the button keeps
                      its width; currentColor spinner reads on the primary
                      fill in both themes. */}
                  <span className={saving ? "invisible" : ""}>{t("Save")}</span>
                  {saving && (
                    <span className="absolute inset-0 flex items-center justify-center">
                      <BeatLoader
                        color="currentColor"
                        loading={true}
                        size={9}
                      />
                    </span>
                  )}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default LocationForm;
